import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  WorkTicket, Station, TicketStatus, Priority, RoutingStep, QCOutcome,
} from '@/types/production';

// ─────────── Tickets ───────────

export type TicketFilters = {
  status?: TicketStatus;
  station?: Station;
  priority?: Priority;
  orderId?: string;
  search?: string;
  limit?: number;
};

export function useTickets(filters: TicketFilters = {}) {
  return useQuery({
    queryKey: ['tickets', filters],
    queryFn: async () =>
      (await api.get<{ items: WorkTicket[] }>('/production/tickets', { params: filters })).data.items,
  });
}

export function useTicket(id: string | undefined) {
  return useQuery({
    queryKey: ['ticket', id],
    queryFn: async () =>
      (await api.get<WorkTicket>(`/production/tickets/${id}`)).data,
    enabled: !!id,
  });
}

export type SaveTicketInput = Omit<WorkTicket,
  'id' | 'number' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' |
  'labor' | 'consumptions' | 'qcChecks' | 'totalLaborMinutes' | 'totalMaterialCost' |
  'startedAt' | 'completedAt' | 'releasedAt' | 'routing'
> & {
  routing: Array<Omit<RoutingStep, 'completed' | 'completedAt' | 'completedBy' | 'completedByName'>>;
};

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveTicketInput) =>
      (await api.post<{ id: string; number: string }>('/production/tickets', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveTicketInput>) =>
      (await api.patch(`/production/tickets/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] });
    },
  });
}

export function useDeleteTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete(`/production/tickets/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}

// ─────────── Status transitions ───────────

export function useReleaseTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/production/tickets/${id}/release`)).data,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket', id] });
    },
  });
}

export function useAdvanceStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) =>
      (await api.post(`/production/tickets/${id}/advance`, { notes })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] });
    },
  });
}

export function useSetTicketStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, reason }: { id: string; status: TicketStatus; reason?: string }) =>
      (await api.post(`/production/tickets/${id}/status`, { status, reason })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] });
    },
  });
}

// ─────────── Labor ───────────

export function useStartLabor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, station }: { id: string; station: Station }) =>
      (await api.post<{ eventId: string }>(`/production/tickets/${id}/labor/start`, { station })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}

export function useStopLabor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, eventId, notes }: { id: string; eventId: string; notes?: string }) =>
      (await api.post(`/production/tickets/${id}/labor/stop`, { eventId, notes })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}

// ─────────── Consumptions ───────────

export function useLogConsumption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id, itemId, quantity, location,
    }: { id: string; itemId: string; quantity: number; location: string }) =>
      (await api.post(`/production/tickets/${id}/consume`, { itemId, quantity, location })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['inventory-item', vars.itemId] });
    },
  });
}

// ─────────── QC ───────────

export function useRecordQC() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id, outcome, defectCategory, reworkStation, notes,
    }: {
      id: string; outcome: QCOutcome; defectCategory?: string;
      reworkStation?: Station; notes?: string;
    }) =>
      (await api.post(`/production/tickets/${id}/qc`, {
        outcome, defectCategory, reworkStation, notes,
      })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] });
    },
  });
}

// ─────────── From order ───────────

export function useCreateTicketsFromOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) =>
      (await api.post<{ created: number; ticketIds: string[] }>(
        '/production/tickets/from-order',
        { orderId },
      )).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}
